import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Loader2,
  RotateCw,
  Eye,
  Download,
  Building2,
  User,
  Search,
  Check,
  AlertTriangle,
  X,
  UserCheck,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import JobCollectionsTab from "../components/JobCollectionsTab";
import AttachmentPreviewModal from "@/components/AttachmentPreviewModal";
import { api, formatApiError, getToken } from "@/lib/api";
import {
  JOB_STATUS_FLOW,
  JOB_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  PRIORITY_LABELS,
  CUSTOMER_TYPE_LABELS,
  jobStatusTone,
  orderStatusTone,
} from "@/lib/constants";
import { profitTone, marginTone } from "@/lib/profit";
import { formatCurrency, formatDate, formatDateTime, toInputDate, fromInputDate } from "@/lib/format";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";

const EMPTY_PRODUCT = { product_category_id: "", brand: "", model: "", color: "", quantity_description: "", unit: "", description: "", amount: "" };

const emptyForm = () => ({
  customer_id: null,
  customer_type: "individual",
  customer_name: "",
  company_name: "",
  phone: "",
  alternative_phone: "",
  email: "",
  city: "",
  district: "",
  address: "",
  salesperson_id: "",
  contract_date: "",
  sales_amount: "0",
  pricing_mode: "ITEM_BASED",
  vat_included: true,
  priority: "normal",
  general_notes: "",
  planned_measurement_date: "",
  actual_measurement_date: "",
  planned_order_date: "",
  actual_order_date: "",
  estimated_delivery_date: "",
  actual_delivery_date: "",
  planned_installation_start: "",
  planned_installation_end: "",
  actual_installation_start: "",
  actual_installation_end: "",
  closing_date: "",
  progress_percentage: 0,
  products: [],
});

export default function JobFormPage() {
  const { id } = useParams();
  const isNew = !id || id === "yeni";
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, settings } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const minMargin = settings?.min_margin_percent ?? 20;

  // Direct mobile access to /isler/yeni should redirect to /isler
  useEffect(() => {
    if (isNew && isMobile) {
      navigate("/isler", { replace: true });
    }
  }, [isNew, isMobile, navigate]);

  const [searchParams] = useSearchParams();
  const preloadCustomerId = searchParams.get("customer_id");

  const [form, setForm] = useState(emptyForm());
  const [activeTab, setActiveTab] = useState("genel");
  const [job, setJob] = useState(null);
  const [products, setProducts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [productCategories, setProductCategories] = useState([]);
  const [salespeople, setSalespeople] = useState([]);

  // Customer selection & duplicate check state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerMode, setCustomerMode] = useState(preloadCustomerId ? "existing" : "existing"); // 'existing' | 'new'
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [ignoredDuplicatePrompt, setIgnoredDuplicatePrompt] = useState(false);

  const [orders, setOrders] = useState([]);
  const [costs, setCosts] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusChangeTo, setStatusChangeTo] = useState("");
  const [statusExplanation, setStatusExplanation] = useState("");
  const [statusClosingDate, setStatusClosingDate] = useState("");

  // Cost form
  const [costForm, setCostForm] = useState({ cost_category_id: "", cost_type: "estimated", amount: "0", description: "", supplier_or_person: "", cost_date: "" });
  const [costCategories, setCostCategories] = useState([]);
  const [convertingPricing, setConvertingPricing] = useState(false);

  const canEdit = useMemo(() => {
    if (isMobile) return false;
    if (isNew) return true;
    if (!job) return false;
    if (isAdmin) return true;
    if (job.is_archived || job.status === "is_kapatildi") return false;
    return user?.role === "sales" || user?.role === "order_manager";
  }, [isMobile, isNew, job, user, isAdmin]);

  const pricingMode = form.pricing_mode || (isNew ? "ITEM_BASED" : (job?.pricing_mode || "LEGACY"));
  const isItemBased = pricingMode === "ITEM_BASED";

  const productsTotal = useMemo(() => {
    return products.reduce((acc, p) => {
      const val = parseFloat(String(p.amount || 0).replace(",", ".").trim());
      return acc + (isNaN(val) ? 0 : val);
    }, 0);
  }, [products]);

  // Keep form.sales_amount in sync with product sum when in item-based pricing mode
  useEffect(() => {
    if (isItemBased) {
      const next = productsTotal.toFixed(2);
      setForm((f) => (f.sales_amount === next ? f : { ...f, sales_amount: next }));
    }
  }, [isItemBased, productsTotal]);

  const handleConvertPricing = async () => {
    if (!job || isNew) return;
    const currentSales = parseFloat(form.sales_amount) || 0;
    if (productsTotal.toFixed(2) !== currentSales.toFixed(2)) {
      toast.error(
        `Kalemler toplamı (${formatCurrency(productsTotal)}) ile mevcut satış tutarı (${formatCurrency(currentSales)}) eşleşmiyor. Fark: ${formatCurrency(productsTotal - currentSales)}. Lütfen önce ürün kalemlerini düzenleyip kaydedin.`
      );
      return;
    }
    if (!window.confirm("Bu işi kalem bazlı fiyatlandırmaya geçirmek istediğinize emin misiniz? Satış tutarı bundan sonra ürün kalemleri toplamından yönetilecektir.")) {
      return;
    }
    setConvertingPricing(true);
    try {
      const res = await api.post(`/jobs/${job.id}/convert-pricing`);
      setJob(res.data);
      setForm((f) => ({ ...f, pricing_mode: "ITEM_BASED", sales_amount: String(res.data.sales_amount) }));
      toast.success("İş kalem bazlı fiyatlandırmaya dönüştürüldü.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConvertingPricing(false);
    }
  };

  const patch = (partial) => {
    if (isMobile) return;
    setForm((f) => ({ ...f, ...partial }));
    setDirty(true);
  };

  // Preload customer from query param if creating new job
  useEffect(() => {
    if (isNew && preloadCustomerId) {
      api.get(`/customers/${preloadCustomerId}`)
        .then((res) => {
          const cust = res.data;
          applySelectedCustomer(cust);
        })
        .catch(() => {});
    }
  }, [isNew, preloadCustomerId]);

  // Handle customer search debounce
  useEffect(() => {
    if (!customerSearchQuery.trim() || customerSearchQuery.trim().length < 2) {
      setCustomerSearchResults([]);
      setIsSearchingCustomer(false);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingCustomer(true);
      try {
        const res = await api.get("/customers", {
          params: { q: customerSearchQuery.trim(), page: 1, page_size: 8, is_active: true },
        });
        setCustomerSearchResults(res.data.items || []);
        setShowCustomerDropdown(true);
      } catch (err) {
        // silent
      } finally {
        setIsSearchingCustomer(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [customerSearchQuery]);

  // Duplicate check when typing new customer info
  useEffect(() => {
    if (!isNew || selectedCustomer || ignoredDuplicatePrompt) {
      setDuplicateWarnings([]);
      return;
    }
    const name = form.customer_name?.trim();
    const phone = form.phone?.trim();
    if ((!name || name.length < 3) && (!phone || phone.length < 5)) {
      setDuplicateWarnings([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.post("/customers/check-duplicates", {
          display_name: name || null,
          company_name: form.company_name?.trim() || null,
          phone: phone || null,
          email: form.email?.trim() || null,
        });
        if (res.data?.matches?.length > 0) {
          setDuplicateWarnings(res.data.matches);
        } else {
          setDuplicateWarnings([]);
        }
      } catch (err) {
        // silent
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [isNew, selectedCustomer, ignoredDuplicatePrompt, form.customer_name, form.company_name, form.phone, form.email]);

  const applySelectedCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomerMode("existing");
    setShowCustomerDropdown(false);
    setCustomerSearchQuery("");
    setDuplicateWarnings([]);
    setForm((f) => ({
      ...f,
      customer_id: cust.id,
      customer_type: cust.customer_type || f.customer_type,
      customer_name: cust.display_name || f.customer_name,
      company_name: cust.company_name || f.company_name,
      phone: cust.phone || f.phone,
      alternative_phone: cust.alternative_phone || f.alternative_phone,
      email: cust.email || f.email,
      city: cust.city || f.city,
      district: cust.district || f.district,
      address: cust.address || f.address,
    }));
    setDirty(true);
    toast.info(`Müşteri seçildi: ${cust.display_name} (${cust.customer_number})`);
  };

  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setCustomerMode("existing");
    setForm((f) => ({
      ...f,
      customer_id: null,
    }));
    setDirty(true);
  };

  const loadReferences = async () => {
    const [pcs, sps, ccs] = await Promise.all([
      api.get("/product-categories"),
      isAdmin ? api.get("/users/salespeople") : Promise.resolve({ data: [] }),
      isAdmin ? api.get("/cost-categories") : Promise.resolve({ data: [] }),
    ]);
    setProductCategories(pcs.data);
    setSalespeople(sps.data);
    setCostCategories(ccs.data);
  };

  const loadJob = async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await api.get(`/jobs/${id}`);
      setJob(r.data);
      if (r.data.customer) {
        setSelectedCustomer(r.data.customer);
      }
      setForm({
        customer_id: r.data.customer_id || (r.data.customer ? r.data.customer.id : null),
        customer_type: r.data.customer_type || "individual",
        customer_name: r.data.customer_name || "",
        company_name: r.data.company_name || "",
        phone: r.data.phone || "",
        alternative_phone: r.data.alternative_phone || "",
        email: r.data.email || "",
        city: r.data.city || "",
        district: r.data.district || "",
        address: r.data.address || "",
        salesperson_id: r.data.salesperson_id || "",
        contract_date: toInputDate(r.data.contract_date),
        sales_amount: String(r.data.sales_amount || "0"),
        pricing_mode: r.data.pricing_mode || "LEGACY",
        vat_included: r.data.vat_included,
        priority: r.data.priority || "normal",
        general_notes: r.data.general_notes || "",
        planned_measurement_date: toInputDate(r.data.planned_measurement_date),
        actual_measurement_date: toInputDate(r.data.actual_measurement_date),
        planned_order_date: toInputDate(r.data.planned_order_date),
        actual_order_date: toInputDate(r.data.actual_order_date),
        estimated_delivery_date: toInputDate(r.data.estimated_delivery_date),
        actual_delivery_date: toInputDate(r.data.actual_delivery_date),
        planned_installation_start: toInputDate(r.data.planned_installation_start),
        planned_installation_end: toInputDate(r.data.planned_installation_end),
        actual_installation_start: toInputDate(r.data.actual_installation_start),
        actual_installation_end: toInputDate(r.data.actual_installation_end),
        closing_date: toInputDate(r.data.closing_date),
        progress_percentage: r.data.progress_percentage || 0,
        products: [],
      });
      setProducts(r.data.products.map((p) => ({
        ...p,
        amount: p.amount != null ? String(p.amount) : "",
      })));
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/isler");
    } finally {
      setLoading(false);
    }
  };

  const loadRelated = async () => {
    if (isNew || !job) return;
    try {
      const jobsRes = await api.get(`/orders`, { params: { job_id: job.id, page_size: 200 } });
      setOrders(jobsRes.data.items || []);
      const hr = await api.get(`/jobs/${job.id}/status-history`);
      setHistory(hr.data);
      const nr = await api.get(`/notes`, { params: { entity_type: "job", entity_id: job.id } });
      setNotes(nr.data);
      const ar = await api.get(`/attachments`, { params: { entity_type: "job", entity_id: job.id } });
      setAttachments(ar.data);
      if (isAdmin) {
        const [cr, sr] = await Promise.all([
          api.get(`/costs/by-job/${job.id}`),
          api.get(`/costs/summary/${job.id}`),
        ]);
        setCosts(cr.data);
        setCostSummary(sr.data);
      }
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    loadReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActiveTab("genel");
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (job) loadRelated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  // Warn on close with unsaved changes
  useEffect(() => {
    const beforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const goBack = () => {
    if (activeTab !== "genel") {
      setActiveTab("genel");
      return;
    }
    if (dirty && !window.confirm("Kaydedilmemiş değişiklikleriniz var. Yine de çıkmak istiyor musunuz?")) return;
    navigate("/isler");
  };

  const submit = async () => {
    if (isMobile) return;
    if (!form.customer_name.trim()) {
      toast.error("Müşteri adı zorunludur.");
      return;
    }
    if (parseFloat(form.sales_amount) < 0) {
      toast.error("Satış tutarı negatif olamaz.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        salesperson_id: form.salesperson_id || null,
        contract_date: form.contract_date ? fromInputDate(form.contract_date) : null,
        planned_measurement_date: fromInputDate(form.planned_measurement_date),
        actual_measurement_date: fromInputDate(form.actual_measurement_date),
        planned_order_date: fromInputDate(form.planned_order_date),
        actual_order_date: fromInputDate(form.actual_order_date),
        estimated_delivery_date: fromInputDate(form.estimated_delivery_date),
        actual_delivery_date: fromInputDate(form.actual_delivery_date),
        planned_installation_start: fromInputDate(form.planned_installation_start),
        planned_installation_end: fromInputDate(form.planned_installation_end),
        actual_installation_start: fromInputDate(form.actual_installation_start),
        actual_installation_end: fromInputDate(form.actual_installation_end),
        closing_date: form.closing_date ? fromInputDate(form.closing_date) : null,
        pricing_mode: pricingMode,
        sales_amount: isItemBased ? productsTotal.toFixed(2) : (form.sales_amount || "0"),
        progress_percentage: parseInt(form.progress_percentage) || 0,
        products: products.map((p) => ({
          product_category_id: p.product_category_id,
          brand: p.brand || null,
          model: p.model || null,
          color: p.color || null,
          quantity_description: p.quantity_description || null,
          unit: p.unit || null,
          description: p.description || null,
          amount: p.amount !== "" && p.amount !== null && p.amount !== undefined
            ? parseFloat(String(p.amount).replace(",", "."))
            : null,
        })).filter((p) => p.product_category_id),
      };
      let saved;
      if (isNew) {
        const r = await api.post("/jobs", payload);
        saved = r.data;
        toast.success(`İş oluşturuldu: ${saved.job_number}`);
      } else {
        payload.version = job.version;
        const r = await api.patch(`/jobs/${job.id}`, payload);
        saved = r.data;
        toast.success("Değişiklikler kaydedildi.");
      }
      setDirty(false);
      if (isNew) navigate(`/isler/${saved.id}`);
      else {
        setJob(saved);
        setForm((f) => ({
          ...f,
          pricing_mode: saved.pricing_mode || "LEGACY",
          sales_amount: String(saved.sales_amount || "0"),
        }));
        setProducts(saved.products.map((p) => ({
          ...p,
          amount: p.amount != null ? String(p.amount) : "",
        })));
      }
    } catch (err) {
      toast.error(formatApiError(err, "Kaydetme başarısız."));
    } finally {
      setSaving(false);
    }
  };

  const addProduct = () => {
    if (isMobile) return;
    setProducts((p) => [...p, { ...EMPTY_PRODUCT }]);
    setDirty(true);
  };
  const removeProduct = (idx) => {
    if (isMobile) return;
    setProducts((p) => p.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const patchProduct = (idx, val) => {
    if (isMobile) return;
    setProducts((p) => p.map((x, i) => (i === idx ? { ...x, ...val } : x)));
    setDirty(true);
  };

  const changeStatus = async () => {
    if (isMobile) return;
    if (!statusChangeTo || statusChangeTo === job.status) return;
    try {
      const r = await api.post(`/jobs/${job.id}/status`, {
        new_status: statusChangeTo,
        explanation: statusExplanation || null,
        version: job.version,
        closing_date: statusClosingDate ? fromInputDate(statusClosingDate) : null,
      });
      toast.success("Durum güncellendi.");
      setJob(r.data);
      setStatusChangeTo("");
      setStatusExplanation("");
      setStatusClosingDate("");
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addNote = async () => {
    if (isMobile) return;
    if (!newNote.trim()) return;
    try {
      await api.post("/notes", { entity_type: "job", entity_id: job.id, note_text: newNote.trim() });
      setNewNote("");
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadFile = async (file) => {
    if (isMobile || !file || !job) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "job");
      fd.append("entity_id", job.id);
      fd.append("file", file);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dosya yüklendi.");
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteJob = async () => {
    if (isMobile) return;
    if (!window.confirm("Bu iş kaydını KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("İş kaydı kalıcı olarak silindi.");
      navigate("/isler");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteCost = async (c) => {
    if (isMobile) return;
    if (!window.confirm("Bu maliyet kaydını KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/costs/${c.id}`);
      toast.success("Maliyet kaydı kalıcı olarak silindi.");
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteAttachment = async (a) => {
    if (isMobile) return;
    if (!window.confirm("Bu dosyayı KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/attachments/${a.id}`);
      toast.success("Dosya kalıcı olarak silindi.");
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteNote = async (n) => {
    if (isMobile) return;
    if (!window.confirm("Bu notu KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/notes/${n.id}`);
      toast.success("Not kalıcı olarak silindi.");
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addCost = async () => {
    if (isMobile) return;
    if (!costForm.cost_category_id || !costForm.amount) return;
    try {
      await api.post("/costs", {
        job_id: job.id,
        cost_category_id: costForm.cost_category_id,
        product_category_id: null,
        cost_type: costForm.cost_type,
        amount: costForm.amount,
        description: costForm.description || null,
        supplier_or_person: costForm.supplier_or_person || null,
        cost_date: costForm.cost_date ? fromInputDate(costForm.cost_date) : null,
      });
      toast.success("Maliyet kaydı eklendi.");
      setCostForm({ cost_category_id: "", cost_type: "estimated", amount: "0", description: "", supplier_or_person: "", cost_date: "" });
      loadRelated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-slate-500 py-16" data-testid="job-loading">
        <Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="job-form-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goBack} className="border-[#DCE2E8]" data-testid="job-back-button">
            <ArrowLeft size={16} className="mr-1" /> {activeTab === "genel" ? "İş Listesine Dön" : "Genel Bilgilere Dön"}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">
              {isNew ? "Yeni İş" : (job?.customer_name ? `${job.job_number} • ${job.customer_name}` : job?.job_number)}
            </h1>
            {!isNew && (
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span>Sürüm v{job?.version}</span>
                {job?.is_archived && <StatusBadge tone="danger">Arşivli</StatusBadge>}
                <StatusBadge tone={jobStatusTone(job?.status)}>{JOB_STATUS_LABELS[job?.status]}</StatusBadge>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="flex items-center gap-2">
            {isAdmin && !isNew && (
              <Button
                variant="destructive"
                onClick={handleDeleteJob}
                data-testid="job-delete-button"
              >
                Kalıcı Olarak Sil
              </Button>
            )}
            {canEdit && (
              <Button onClick={submit} disabled={saving} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm" data-testid="job-save-button">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Kaydediliyor</> : <><Save size={16} className="mr-2" /> Kaydet</>}
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border border-[#DCE2E8] flex flex-nowrap overflow-x-auto w-full justify-start h-auto p-1 shadow-sm scrollbar-thin">
          <TabsTrigger value="genel" data-testid="tab-general" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Genel Bilgiler</TabsTrigger>
          <TabsTrigger value="urunler" data-testid="tab-products" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Ürünler</TabsTrigger>
          <TabsTrigger value="surec" data-testid="tab-process" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Süreç</TabsTrigger>
          <TabsTrigger value="siparisler" data-testid="tab-orders" disabled={isNew} className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Siparişler</TabsTrigger>
          <TabsTrigger value="maliyetler" data-testid="tab-costs" disabled={isNew} className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Maliyetler</TabsTrigger>
          <TabsTrigger value="dosyalar" data-testid="tab-files" disabled={isNew} className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Dosyalar</TabsTrigger>
          <TabsTrigger value="notlar" data-testid="tab-notes" disabled={isNew} className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Notlar</TabsTrigger>
          <TabsTrigger value="tahsilat" data-testid="tab-tahsilat" disabled={isNew} className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Tahsilat & Alacak</TabsTrigger>
          <TabsTrigger value="gecmis" data-testid="tab-history" disabled={isNew} className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">İşlem Geçmişi</TabsTrigger>
        </TabsList>

        {/* ---------- Genel Bilgiler ---------- */}
        <TabsContent value="genel" className="space-y-4">
          {/* Müşteri Belirleme Adımı (1. Adım) */}
          <div className="rounded-lg border border-[#DCE2E8] bg-white p-5 shadow-sm space-y-4" data-testid="job-customer-selector-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#FF353A]" />
                  1. Adım: Müşteri Belirleme
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  İşi mevcut kayıtlı bir müşteriye bağlayabilir veya sıfırdan yeni bir müşteri oluşturabilirsiniz.
                </p>
              </div>
              {selectedCustomer && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <UserCheck className="w-3.5 h-3.5" />
                    Kayıtlı Müşteriye Bağlı
                  </span>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearSelectedCustomer}
                      className="text-xs text-slate-500 hover:text-red-600 h-7 px-2"
                      data-testid="job-change-customer-btn"
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Değiştir / Kaldır
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Yeni İş Açarken Seçenek Düğmeleri (A: Mevcut Müşteri, B: Yeni Müşteri) */}
            {isNew && !selectedCustomer && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setCustomerMode("existing")}
                  className={`p-3.5 rounded-lg border text-left transition-all flex items-start gap-3 ${
                    customerMode === "existing"
                      ? "border-[#FF353A] bg-red-50/30 ring-1 ring-[#FF353A]"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  data-testid="btn-choose-existing-customer"
                >
                  <div className={`p-2 rounded-md shrink-0 ${customerMode === "existing" ? "bg-[#FF353A] text-white" : "bg-slate-100 text-slate-600"}`}>
                    <Search className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-800">A. Kayıtlı Müşteri Seç</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Sistemde mevcut olan cariyi arayıp bu işe bağlayın.</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCustomerMode("new");
                    setSelectedCustomer(null);
                    setForm((f) => ({ ...f, customer_id: null }));
                  }}
                  className={`p-3.5 rounded-lg border text-left transition-all flex items-start gap-3 ${
                    customerMode === "new"
                      ? "border-[#FF353A] bg-red-50/30 ring-1 ring-[#FF353A]"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  data-testid="btn-choose-new-customer"
                >
                  <div className={`p-2 rounded-md shrink-0 ${customerMode === "new" ? "bg-[#FF353A] text-white" : "bg-slate-100 text-slate-600"}`}>
                    <UserPlus className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-800">B. Yeni Müşteri Oluştur</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Bu iş için sıfırdan yeni bağımsız bir müşteri kaydı açın.</div>
                  </div>
                </button>
              </div>
            )}

            {/* Arama Kutusu: customerMode === 'existing' iken */}
            {canEdit && !selectedCustomer && customerMode === "existing" && (
              <div className="relative pt-1">
                <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Müşteri Arama</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Müşteri adı, firma veya telefon ile arayın..."
                    value={customerSearchQuery}
                    onChange={(e) => setCustomerSearchQuery(e.target.value)}
                    onFocus={() => { if (customerSearchResults.length > 0) setShowCustomerDropdown(true); }}
                    className="pl-9 pr-8 text-sm border-[#DCE2E8] bg-slate-50/50 focus:bg-white"
                    data-testid="customer-search-input"
                  />
                  {isSearchingCustomer && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                  )}
                </div>

                {/* Sonuç Listesi Dropdown */}
                {showCustomerDropdown && customerSearchResults.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#DCE2E8] rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {customerSearchResults.map((cust) => (
                      <div
                        key={cust.id}
                        onClick={() => applySelectedCustomer(cust)}
                        className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0 flex items-center justify-between transition-colors"
                        data-testid={`customer-option-${cust.id}`}
                      >
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-800">{cust.display_name}</span>
                            <span className="text-[11px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                              {cust.customer_number}
                            </span>
                            {cust.company_name && (
                              <span className="text-xs text-slate-500">({cust.company_name})</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-3">
                            {cust.phone && <span>Tel: {cust.phone}</span>}
                            {cust.city && <span>{cust.city}{cust.district ? ` / ${cust.district}` : ""}</span>}
                          </div>
                        </div>
                        <Button type="button" size="sm" variant="outline" className="text-xs h-7 border-[#DCE2E8]">
                          Seç
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Seçili Müşteri Bilgi Kartı */}
            {selectedCustomer && (
              <div className="bg-slate-50 rounded-lg p-3.5 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 text-sm">{selectedCustomer.display_name}</span>
                    <span className="text-xs font-mono bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                      {selectedCustomer.customer_number}
                    </span>
                    {selectedCustomer.company_name && (
                      <span className="text-xs text-slate-500">({selectedCustomer.company_name})</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {selectedCustomer.phone && <span>Tel: {selectedCustomer.phone}</span>}
                    {selectedCustomer.email && <span>E-posta: {selectedCustomer.email}</span>}
                    {selectedCustomer.city && (
                      <span>Adres: {selectedCustomer.city}{selectedCustomer.district ? ` / ${selectedCustomer.district}` : ""}</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/musteriler/${selectedCustomer.id}`)}
                    className="text-xs h-8 border-[#DCE2E8] hover:bg-white"
                    data-testid="btn-go-to-customer-card"
                  >
                    Müşteri Kartına Git
                  </Button>
                </div>
              </div>
            )}

            {/* Yeni Müşteri Modu Bildirimi */}
            {isNew && !selectedCustomer && customerMode === "new" && (
              <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3.5 text-xs text-blue-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>
                    Yeni müşteri modu aktif. Aşağıdaki formu doldurarak işi kaydettiğinizde yeni bir müşteri kartı da otomatik oluşturulacaktır.
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomerMode("existing")}
                  className="text-blue-700 hover:text-blue-900 h-6 px-2 text-xs font-medium"
                >
                  Kayıtlı Müşteri Ara
                </Button>
              </div>
            )}

            {/* Potansiyel Mükerrer Kayıt Uyarısı (Yeni Müşteri Girilirken) */}
            {isNew && !selectedCustomer && duplicateWarnings.length > 0 && !ignoredDuplicatePrompt && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="duplicate-warning-banner">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-1 flex-1">
                    <h4 className="text-sm font-semibold text-amber-900">
                      Benzer Müşteri Bulundu ({duplicateWarnings.length} eşleşme)
                    </h4>
                    <p className="text-xs text-amber-700">
                      Girdiğiniz isim veya telefon bilgisi sistemdeki mevcut bir müşteriyle eşleşiyor olabilir. 
                      İşi mevcut müşteriye bağlamak mı yoksa yeni bir müşteri olarak mı açmak istersiniz?
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIgnoredDuplicatePrompt(true)}
                    className="text-amber-700 hover:text-amber-900 h-6 px-1.5 text-xs"
                  >
                    Yoksay
                  </Button>
                </div>

                <div className="space-y-2 pt-1">
                  {duplicateWarnings.slice(0, 3).map((match) => (
                    <div
                      key={match.id}
                      className="bg-white rounded p-2.5 border border-amber-200 flex items-center justify-between text-xs"
                    >
                      <div>
                        <div className="font-medium text-slate-800 flex items-center gap-2">
                          {match.display_name}
                          <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1 rounded">
                            {match.customer_number}
                          </span>
                          <span className="text-[11px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-full font-normal">
                            Eşleşme: {match.match_reasons.join(", ")}
                          </span>
                        </div>
                        <div className="text-slate-500 mt-0.5">
                          {match.phone && <span className="mr-3">Tel: {match.phone}</span>}
                          {match.company_name && <span>Firma: {match.company_name}</span>}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => applySelectedCustomer(match)}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7 px-3"
                        data-testid={`match-select-btn-${match.id}`}
                      >
                        Bu Müşteriyi Seç
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 grid gap-4 md:grid-cols-3 shadow-sm">
            <Field label="Müşteri Türü">
              <Select value={form.customer_type} onValueChange={(v) => patch({ customer_type: v })} disabled={!canEdit}>
                <SelectTrigger className="border-[#DCE2E8]" data-testid="job-customer-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#DCE2E8]">
                  {Object.entries(CUSTOMER_TYPE_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Müşteri Adı *">
              <Input value={form.customer_name} onChange={(e) => patch({ customer_name: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8] focus-visible:ring-[#FF353A]" data-testid="job-customer-name" />
            </Field>
            <Field label="Firma / Şirket">
              <Input value={form.company_name} onChange={(e) => patch({ company_name: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8] focus-visible:ring-[#FF353A]" data-testid="job-company-name" />
            </Field>
            <Field label="Telefon">
              <Input value={form.phone} onChange={(e) => patch({ phone: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8] focus-visible:ring-[#FF353A]" data-testid="job-phone" />
            </Field>
            <Field label="Alternatif Telefon">
              <Input value={form.alternative_phone} onChange={(e) => patch({ alternative_phone: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" />
            </Field>
            <Field label="E-posta">
              <Input value={form.email} onChange={(e) => patch({ email: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" />
            </Field>
            <Field label="Şehir">
              <Input value={form.city} onChange={(e) => patch({ city: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" />
            </Field>
            <Field label="İlçe">
              <Input value={form.district} onChange={(e) => patch({ district: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" />
            </Field>
            <Field label="Adres" full>
              <Textarea value={form.address} onChange={(e) => patch({ address: e.target.value })} disabled={!canEdit} readOnly={isMobile} rows={2} className="border-[#DCE2E8]" />
            </Field>
            {isAdmin && (
              <Field label="Satış Personeli">
                <Select value={form.salesperson_id || "__none__"} onValueChange={(v) => patch({ salesperson_id: v === "__none__" ? "" : v })} disabled={!canEdit}>
                  <SelectTrigger className="border-[#DCE2E8]" data-testid="job-salesperson"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#DCE2E8]">
                    <SelectItem value="__none__">— Seçilmedi —</SelectItem>
                    {salespeople.map((sp) => (
                      <SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Sözleşme Tarihi">
              <Input type="date" value={form.contract_date} onChange={(e) => patch({ contract_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" />
            </Field>
            <Field label="Satış Tutarı (KDV Dahil)">
              <div className="space-y-1">
                <Input
                  inputMode="decimal"
                  value={form.sales_amount}
                  onChange={(e) => patch({ sales_amount: e.target.value.replace(",", ".") })}
                  disabled={!canEdit || isItemBased}
                  readOnly={isMobile || isItemBased}
                  className={`border-[#DCE2E8] font-mono ${isItemBased ? "bg-slate-50 cursor-not-allowed text-slate-700" : ""}`}
                  data-testid="job-sales-amount"
                />
                {isItemBased ? (
                  <p className="text-[11px] text-emerald-700 flex items-center gap-1.5 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                    Kalem bazlı fiyatlandırma etkin. Toplam tutar ürün kalemlerinden hesaplanır.
                  </p>
                ) : (
                  <div className="space-y-1">
                    <p className="text-[11px] text-amber-700 flex items-center gap-1.5 font-medium">
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
                      Geçmiş iş — manuel toplam satış tutarı geçerlidir.
                    </p>
                    {canEdit && !isNew && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleConvertPricing}
                        disabled={convertingPricing}
                        className="text-xs h-7 border-amber-300 text-amber-800 hover:bg-amber-50"
                        data-testid="convert-pricing-btn"
                      >
                        {convertingPricing ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                        Kalem Bazlı Fiyatlandırmaya Geçir
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Field>
            <Field label="Öncelik">
              <Select value={form.priority} onValueChange={(v) => patch({ priority: v })} disabled={!canEdit}>
                <SelectTrigger className="border-[#DCE2E8]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#DCE2E8]">
                  {Object.entries(PRIORITY_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="İlerleme %">
              <Input type="number" min={0} max={100} value={form.progress_percentage} onChange={(e) => patch({ progress_percentage: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" />
            </Field>
            <Field label="Genel Notlar" full>
              <Textarea value={form.general_notes} onChange={(e) => patch({ general_notes: e.target.value })} disabled={!canEdit} readOnly={isMobile} rows={3} className="border-[#DCE2E8]" />
            </Field>
          </div>
        </TabsContent>

        {/* ---------- Ürünler ---------- */}
        <TabsContent value="urunler">
          <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 shadow-sm" data-testid="tab-products-content">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-[#171A1F]">Ürün Grupları</h2>
                <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-2">
                  <span>Toplam Kalem Tutarı: <strong className="font-mono text-slate-800">{formatCurrency(productsTotal)}</strong></span>
                  {isItemBased ? (
                    <span className="inline-flex items-center text-emerald-700 font-medium">
                      (Satış tutarına otomatik yansıtılır)
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-amber-700">
                      (Mevcut Satış Tutarı: {formatCurrency(parseFloat(form.sales_amount) || 0)})
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isItemBased && canEdit && !isNew && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleConvertPricing}
                    disabled={convertingPricing}
                    className="border-amber-300 text-amber-800 hover:bg-amber-50 text-xs"
                    data-testid="convert-pricing-products-btn"
                  >
                    {convertingPricing ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                    Kalem Bazlı Fiyatlandırmaya Geçir
                  </Button>
                )}
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={addProduct} className="border-[#DCE2E8]" data-testid="add-product-button">
                    <Plus size={14} className="mr-1" /> Ürün Ekle
                  </Button>
                )}
              </div>
            </div>
            {products.length === 0 ? (
              <EmptyState title="Ürün eklenmemiş" description="Bu işe bir veya daha fazla ürün grubu ekleyin." />
            ) : (
              <div className="space-y-4">
                {products.map((p, idx) => (
                  <div key={idx} className="rounded-md border border-[#DCE2E8] p-4 grid gap-3 md:grid-cols-12" data-testid={`product-row-${idx}`}>
                    <Field label="Ürün Grubu" cols={4}>
                      <Select value={p.product_category_id || "__none__"} onValueChange={(v) => patchProduct(idx, { product_category_id: v === "__none__" ? "" : v })} disabled={!canEdit}>
                        <SelectTrigger className="border-[#DCE2E8]"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent className="bg-white border-[#DCE2E8]">
                          <SelectItem value="__none__">— Seçiniz —</SelectItem>
                          {productCategories.map((pc) => (
                            <SelectItem key={pc.id} value={pc.id}>{pc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Marka" cols={2}><Input value={p.brand || ""} onChange={(e) => patchProduct(idx, { brand: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                    <Field label="Model" cols={2}><Input value={p.model || ""} onChange={(e) => patchProduct(idx, { model: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                    <Field label="Renk" cols={2}><Input value={p.color || ""} onChange={(e) => patchProduct(idx, { color: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                    <Field label="Birim" cols={2}><Input value={p.unit || ""} onChange={(e) => patchProduct(idx, { unit: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                    <Field label="Miktar Açıklaması" cols={3}><Input value={p.quantity_description || ""} onChange={(e) => patchProduct(idx, { quantity_description: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                    <Field label="Açıklama" cols={5}><Input value={p.description || ""} onChange={(e) => patchProduct(idx, { description: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                    <Field label="Ürün Tutarı (TL)" cols={3}>
                      <Input
                        inputMode="decimal"
                        value={p.amount || ""}
                        placeholder="0.00"
                        onChange={(e) => patchProduct(idx, { amount: e.target.value.replace(",", ".") })}
                        disabled={!canEdit}
                        readOnly={isMobile}
                        className="border-[#DCE2E8] font-mono"
                        data-testid={`product-amount-${idx}`}
                      />
                    </Field>
                    {canEdit && (
                      <div className="flex items-end justify-end md:col-span-1">
                        <Button variant="ghost" size="sm" onClick={() => removeProduct(idx)} className="text-rose-600 hover:text-rose-700" data-testid={`remove-product-${idx}`}>
                          <Trash2 size={14} className="mr-1" /> Sil
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ---------- Süreç ---------- */}
        <TabsContent value="surec">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 shadow-sm">
              <h2 className="text-lg font-bold text-[#171A1F]">Termin Tarihleri</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Planlanan Ölçü Tarihi"><Input type="date" value={form.planned_measurement_date} onChange={(e) => patch({ planned_measurement_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Gerçek Ölçü Tarihi"><Input type="date" value={form.actual_measurement_date} onChange={(e) => patch({ actual_measurement_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Planlanan Sipariş Tarihi"><Input type="date" value={form.planned_order_date} onChange={(e) => patch({ planned_order_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Gerçek Sipariş Tarihi"><Input type="date" value={form.actual_order_date} onChange={(e) => patch({ actual_order_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Tahmini Teslim"><Input type="date" value={form.estimated_delivery_date} onChange={(e) => patch({ estimated_delivery_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Gerçek Teslim"><Input type="date" value={form.actual_delivery_date} onChange={(e) => patch({ actual_delivery_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Montaj Başlangıç (Plan)"><Input type="date" value={form.planned_installation_start} onChange={(e) => patch({ planned_installation_start: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Montaj Bitiş (Plan)"><Input type="date" value={form.planned_installation_end} onChange={(e) => patch({ planned_installation_end: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Montaj Başlangıç (Gerçek)"><Input type="date" value={form.actual_installation_start} onChange={(e) => patch({ actual_installation_start: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Montaj Bitiş (Gerçek)"><Input type="date" value={form.actual_installation_end} onChange={(e) => patch({ actual_installation_end: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
                <Field label="Kapanış Tarihi"><Input type="date" value={form.closing_date} onChange={(e) => patch({ closing_date: e.target.value })} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
              </div>
            </div>
            {!isNew && !isMobile && (
              <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 shadow-sm">
                <h2 className="text-lg font-bold text-[#171A1F]">Durum Değiştir</h2>
                <p className="text-sm text-slate-500">Şu an: <StatusBadge tone={jobStatusTone(job?.status)}>{JOB_STATUS_LABELS[job?.status]}</StatusBadge></p>
                <Field label="Yeni Durum">
                  <Select value={statusChangeTo || "__none__"} onValueChange={(v) => setStatusChangeTo(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="status-change-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="__none__">— Seçiniz —</SelectItem>
                      {JOB_STATUS_FLOW.map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {statusChangeTo === "is_kapatildi" && (
                  <Field label="Kapanış Tarihi (Opsiyonel)">
                    <Input type="date" value={statusClosingDate} onChange={(e) => setStatusClosingDate(e.target.value)} className="border-[#DCE2E8]" />
                  </Field>
                )}
                <Field label="Açıklama">
                  <Textarea rows={2} value={statusExplanation} onChange={(e) => setStatusExplanation(e.target.value)} className="border-[#DCE2E8]" />
                </Field>
                <Button onClick={changeStatus} disabled={!statusChangeTo || statusChangeTo === job?.status} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm" data-testid="status-change-button">
                  <RotateCw size={14} className="mr-1" /> Durumu Güncelle
                </Button>
                {job?.status === "is_kapatildi" && !isAdmin && (
                  <p className="text-xs text-rose-600 font-medium">Kapatılmış iş yalnızca yönetici tarafından yeniden açılabilir.</p>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ---------- Siparişler ---------- */}
        <TabsContent value="siparisler">
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-[#DCE2E8] shadow-sm">
              <h3 className="text-md font-bold text-[#171A1F]">İlişkili Siparişler</h3>
              {!isMobile && (
                <Button
                  onClick={() => navigate(`/siparisler/yeni?job_id=${job?.id}`)}
                  className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm"
                  data-testid="add-order-shortcut-button"
                >
                  <Plus size={14} className="mr-1" /> Sipariş Ekle
                </Button>
              )}
            </div>
            <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
              {orders.length === 0 ? (
                <EmptyState title="Bu işe bağlı sipariş yok" description="Sipariş eklemek için yukarıdaki 'Sipariş Ekle' butonunu kullanabilirsiniz." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş No</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tedarikçi</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ürün Grubu</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş Tarihi</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tahmini Teslim</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Gecikme</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((o) => (
                      <TableRow key={o.id} className="gp-row cursor-pointer border-b border-[#DCE2E8]" onClick={() => navigate(`/siparisler/${o.id}`)}>
                        <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{o.order_number}</TableCell>
                        <TableCell className="text-sm text-[#171A1F]">{o.supplier_name || "-"}</TableCell>
                        <TableCell className="text-sm text-slate-700">{o.product_category_name || "-"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(o.order_date)}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(o.estimated_delivery_date)}</TableCell>
                        <TableCell><StatusBadge tone={orderStatusTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</StatusBadge></TableCell>
                        <TableCell>{o.is_delayed ? <StatusBadge tone="danger">Gecikmiş</StatusBadge> : <span className="text-xs text-slate-400">-</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ---------- Maliyetler ---------- */}
        <TabsContent value="maliyetler">
            <div className="space-y-4">
              {costSummary && (
                <div className="grid gap-4 md:grid-cols-4 rounded-lg border border-[#DCE2E8] bg-white p-6 shadow-sm">
                  <Metric label="Satış Tutarı" value={formatCurrency(costSummary.sales_amount)} />
                  <Metric label="Toplam Maliyet" value={formatCurrency(costSummary.total_cost)} />
                  <ProfitMetric label="Brüt Kâr" value={costSummary.gross_profit} />
                  <MarginMetric label="Brüt Kâr Marjı" value={costSummary.gross_margin} minMargin={minMargin} />
                </div>
              )}
              {!isMobile && (
                <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 shadow-sm">
                  <h3 className="text-md font-bold text-[#171A1F]">Yeni Maliyet Kaydı</h3>
                  <div className="grid gap-3 md:grid-cols-6">
                    <Field label="Kategori" cols={2}>
                      <Select value={costForm.cost_category_id || "__none__"} onValueChange={(v) => setCostForm((f) => ({ ...f, cost_category_id: v === "__none__" ? "" : v }))}>
                        <SelectTrigger className="border-[#DCE2E8]" data-testid="cost-category-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent className="bg-white border-[#DCE2E8]">
                          <SelectItem value="__none__">— Seçiniz —</SelectItem>
                          {costCategories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Maliyet Tutarı"><Input inputMode="decimal" value={costForm.amount} onChange={(e) => setCostForm((f) => ({ ...f, amount: e.target.value.replace(",", ".") }))} className="border-[#DCE2E8] font-mono" data-testid="cost-amount-input" /></Field>
                    <Field label="Tarih"><Input type="date" value={costForm.cost_date} onChange={(e) => setCostForm((f) => ({ ...f, cost_date: e.target.value }))} className="border-[#DCE2E8]" /></Field>
                    <Field label="Tedarikçi / Kişi" cols={2}><Input value={costForm.supplier_or_person} onChange={(e) => setCostForm((f) => ({ ...f, supplier_or_person: e.target.value }))} className="border-[#DCE2E8]" /></Field>
                    <Field label="Açıklama" cols={5}><Input value={costForm.description} onChange={(e) => setCostForm((f) => ({ ...f, description: e.target.value }))} className="border-[#DCE2E8]" /></Field>
                    <div className="flex items-end">
                      <Button onClick={addCost} className="w-full bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm" data-testid="cost-add-button"><Plus size={14} className="mr-1" /> Ekle</Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
                {costs.length === 0 ? (
                  <EmptyState title="Maliyet kaydı yok" description="Bu iş için bir maliyet kaydı ekleyin." />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tarih</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Kategori</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Açıklama</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tedarikçi/Kişi</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Maliyet Tutarı</TableHead>
                        {isAdmin && !isMobile && <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlem</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costs.map((c) => (
                        <TableRow key={c.id} className="border-b border-[#DCE2E8]" data-testid={`cost-row-${c.id}`}>
                          <TableCell className="text-sm text-slate-600">{formatDate(c.cost_date)}</TableCell>
                          <TableCell className="text-sm font-medium text-[#171A1F]">{c.cost_category_name || "-"}</TableCell>
                          <TableCell className="text-sm text-slate-700">{c.description || "-"}</TableCell>
                          <TableCell className="text-sm text-slate-700">{c.supplier_or_person || "-"}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-medium">{formatCurrency(c.amount)}</TableCell>
                          {isAdmin && !isMobile && (
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-rose-600 hover:text-rose-700"
                                onClick={() => handleDeleteCost(c)}
                                data-testid={`cost-delete-${c.id}`}
                              >
                                Sil
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          </TabsContent>

        {/* ---------- Dosyalar ---------- */}
        <TabsContent value="dosyalar">
          <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 shadow-sm">
            {canEdit && !isMobile && (
              <div>
                <input
                  type="file"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                  className="text-sm"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx"
                  data-testid="file-input"
                />
                {uploading && <p className="text-xs text-slate-500 mt-2">Yükleniyor...</p>}
                <p className="text-xs text-slate-500 mt-2">İzin verilen: PDF, JPG, PNG, XLSX, DOCX. En fazla 20 MB.</p>
              </div>
            )}
            {attachments.length === 0 ? (
              <EmptyState title="Dosya yok" description="Bu işe henüz dosya eklenmemiş." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Dosya</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tür</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Boyut</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tarih</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attachments.map((a) => (
                    <TableRow key={a.id} className="border-b border-[#DCE2E8]" data-testid={`attachment-row-${a.id}`}>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => setPreviewAttachment(a)}
                          className="text-[#168DEB] hover:underline font-medium text-left flex items-center gap-1.5 focus:outline-none"
                          data-testid={`attachment-preview-${a.id}`}
                          title="Önizlemek için tıklayın"
                        >
                          <Eye size={14} className="shrink-0 text-slate-400" />
                          <span>{a.original_filename}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{a.mime_type}</TableCell>
                      <TableCell className="text-xs font-mono">{(a.file_size / 1024).toFixed(1)} KB</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDateTime(a.created_at)}</TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          asChild
                          className="h-8 px-2 text-slate-600 hover:text-slate-900"
                          title="İndir"
                        >
                          <a
                            href={`${api.defaults.baseURL}/attachments/${a.id}/download?token=${getToken()}`}
                            download={a.original_filename}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`attachment-download-${a.id}`}
                          >
                            <Download size={14} className="mr-1 inline" />
                            İndir
                          </a>
                        </Button>
                        {isAdmin && !isMobile && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-rose-600 hover:text-rose-700"
                            onClick={() => handleDeleteAttachment(a)}
                            data-testid={`attachment-delete-${a.id}`}
                          >
                            Sil
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* ---------- Notlar ---------- */}
        <TabsContent value="notlar">
          <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 shadow-sm">
            {canEdit && !isMobile && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Yeni Not</Label>
                  <Textarea rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} className="border-[#DCE2E8]" data-testid="note-input" />
                </div>
                <Button onClick={addNote} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm" data-testid="note-add-button"><Plus size={14} className="mr-1" /> Ekle</Button>
              </div>
            )}
            {notes.length === 0 ? (
              <EmptyState title="Not yok" description="İş sürecine dair notlar buraya eklenir." />
            ) : (
              <ul className="space-y-3">
                {notes.map((n) => (
                  <li key={n.id} className="rounded-md border border-[#DCE2E8] p-3 flex justify-between items-start" data-testid={`note-row-${n.id}`}>
                    <div>
                      <div className="text-sm text-[#171A1F] whitespace-pre-wrap">{n.note_text}</div>
                      <div className="text-xs text-slate-500 mt-1">{n.created_by_name || "-"} · {formatDateTime(n.created_at)}</div>
                    </div>
                    {isAdmin && !isMobile && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600 hover:text-rose-700 shrink-0"
                        onClick={() => handleDeleteNote(n)}
                        data-testid={`note-delete-${n.id}`}
                      >
                        Sil
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* ---------- İşlem Geçmişi ---------- */}
        <TabsContent value="tahsilat" className="m-0 focus-visible:outline-none">
            <div className="py-4 md:py-6">
              <JobCollectionsTab jobId={id} job={job} isMobile={isMobile} />
            </div>
          </TabsContent>
          <TabsContent value="gecmis">
          <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
            {history.length === 0 ? (
              <EmptyState title="Geçmiş yok" description="Bu iş için henüz durum değişikliği kaydı yok." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tarih</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Eski Durum</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Yeni Durum</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Kullanıcı</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Açıklama</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id} className="border-b border-[#DCE2E8]">
                      <TableCell className="text-sm text-slate-600">{formatDateTime(h.changed_at)}</TableCell>
                      <TableCell>{h.previous_status ? <StatusBadge tone={jobStatusTone(h.previous_status)}>{JOB_STATUS_LABELS[h.previous_status]}</StatusBadge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                      <TableCell><StatusBadge tone={jobStatusTone(h.new_status)}>{JOB_STATUS_LABELS[h.new_status]}</StatusBadge></TableCell>
                      <TableCell className="text-sm text-[#171A1F]">{h.changed_by_name || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{h.explanation || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Mobile Sticky Action Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-[#DCE2E8] p-3 z-30 flex items-center justify-between gap-3 shadow-lg pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={goBack}
          className="border-[#DCE2E8] min-h-[44px] w-full"
          data-testid="job-mobile-back-button"
        >
          <ArrowLeft size={16} className="mr-1" /> Geri
        </Button>
      </div>

      <AttachmentPreviewModal
        attachment={previewAttachment}
        open={!!previewAttachment}
        onOpenChange={(open) => !open && setPreviewAttachment(null)}
        token={getToken()}
      />
    </div>
  );
}

function Field({ label, children, full, cols, className = "" }) {
  const colMap = {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    5: "md:col-span-5",
    6: "md:col-span-6",
    12: "md:col-span-12",
  };
  const cls = full ? "md:col-span-3" : cols ? (colMap[cols] || `md:col-span-${cols}`) : "";
  return (
    <div className={`space-y-1.5 ${cls} ${className}`}>
      <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className="rounded-md border border-[#DCE2E8] bg-[#F4F6F8] p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg font-bold ${tone === "danger" ? "text-rose-700" : tone === "success" ? "text-emerald-700" : "text-[#171A1F]"}`}>{value}</div>
    </div>
  );
}

function ProfitMetric({ label, value, inverse = false }) {
  const n = value == null ? 0 : parseFloat(value);
  let tone, badge;
  if (inverse) {
    if (n > 0) { tone = "text-rose-700"; badge = "Aşıldı"; }
    else if (n < 0) { tone = "text-emerald-700"; badge = "Altında"; }
    else { tone = "text-slate-700"; badge = "Sıfır"; }
  } else {
    const t = profitTone(value);
    tone = t.className;
    badge = t.label;
  }
  return (
    <div className="rounded-md border border-[#DCE2E8] bg-[#F4F6F8] p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg font-bold ${tone}`}>{formatCurrency(value)}</div>
      <div className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${tone}`}>{badge}</div>
    </div>
  );
}

function MarginMetric({ label, value, minMargin }) {
  const { className, label: badge } = marginTone(value, minMargin);
  const display = value == null ? "-" : `%${value}`;
  return (
    <div className="rounded-md border border-[#DCE2E8] bg-[#F4F6F8] p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg font-bold ${className}`}>{display}</div>
      <div className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${className}`}>{badge}</div>
    </div>
  );
}
